import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Space,
  Button,
  Tag,
  Typography,
  Divider,
  Spin,
  message,
  Modal,
  Input,
  Row,
  Col,
  theme,
} from "antd";
import { Plug, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import QuickBooksIntegration from "../integrations/quickBooksIntegration";
import { CorporationOperation } from "../../../../../serviceoperation/CorporationOperation";
import { CompanyContext } from "../../../../../contexts/company-context";
import { IntegrationOperation } from "../../../../../serviceoperation/IntegrationOperation";

const { Text, Paragraph } = Typography;

interface Integration {
  id: string;
  provider: string;
  isConnected: boolean;
  connectedAt?: string;
  realmId?: string;
}
interface IntegrationsTabProps {
  msg?: string | null;
}

const IntegrationsTab: React.FC<IntegrationsTabProps> = ({ msg }) => {
  const { token } = theme.useToken();
  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: "1",
      provider: "quickbooks",
      isConnected: false,
    },
  ]);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [loadingQuickBooks, setLoadingQuickBooks] = useState(true);
  const { selectedCompany } = useContext(CompanyContext);

  useEffect(() => {
    setLoadingQuickBooks(true);
    const corp = new CorporationOperation();
    corp
      .getCorpIntegrationsAync(selectedCompany?.corporationId as number)
      .then((response) => {
        if (response?.statusCode === 200 && response?.data) {
          // Map response data to Integration interface
          const fetchedIntegrations = {
            id: response.data.integrationId.toString(),
            provider: response.data.integrationName,
            isConnected: true,
            connectedAt: response.data.createdDateTimeUTC,
          };
          setIntegrations([fetchedIntegrations]);
          setLoadingQuickBooks(false);
        }
        setLoadingQuickBooks(false);
      });
  }, []);

  // Show success or error message based on msg prop
  // this is for showing message after redirect from quickbooks oauth flow
  useEffect(() => {
    if (msg === "sucessful") {
      message.success("Successfully Linked QuickBooks account!");
    }
    if (msg === "failed") {
      message.error("Failed to link QuickBooks account. Please try again.");
    }
  }, []);

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const intOps = new IntegrationOperation();
      await intOps
        .integrationStatusAsync(selectedCompany?.corporationId as number)
        .then((response) => {
          if (response?.statusCode === 200) {
            message.success("QuickBooks connection is working correctly!");
          } else if (response?.statusCode === 418) {
            message.error(
              "QuickBooks connection test failed. Please Reconnect your account."
            );
          } else {
            message.error(
              "Failed to test connection. Please check your credentials."
            );
          }
        });
      setShowTestModal(false);
    } catch (error) {
      message.error(
        "Failed to test connection. Please check your credentials."
      );
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDisconnect = (providerId: string) => {
    Modal.confirm({
      title: "Disconnect Integration",
      content:
        "Are you sure you want to disconnect this integration? This will stop syncing data.",
      okText: "Disconnect",
      okType: "danger",
      onOk() {
        const corpInt = new IntegrationOperation();
        corpInt
          .deleteCorpIntegrationAsync(selectedCompany?.corporationId as number)
          .then((response) => {
            if (response?.statusCode === 200) {
              setIntegrations(
                integrations.map((int) =>
                  int.id === providerId ? { ...int, isConnected: false } : int
                )
              );
              message.success("Integration disconnected");
            } else {
              message.error("Failed to disconnect integration.");
            }
          })
          .catch(() => {
            message.error("Failed to disconnect integration.");
          });
      },
    });
  };

  return (
    <div className="max-w-4xl">
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <div
          style={{
            padding: "16px",
            backgroundColor: token.colorInfoBg,
            borderColor: token.colorInfoBorder,
            border: `1px solid ${token.colorInfoBorder}`,
            borderRadius: 8,
          }}
        >
          <Space align="start" style={{ width: "100%" }}>
            <AlertCircle
              style={{ color: token.colorInfo, marginTop: 2, flexShrink: 0 }}
              size={20}
            />
            <div>
              <Text strong style={{ color: token.colorInfo }}>
                Integration Management
              </Text>
              <Paragraph style={{ margin: "4px 0 0 0", fontSize: 13 }}>
                Connect third-party services to sync payroll, employee data, and
                other information. Only administrators can manage integrations.
              </Paragraph>
            </div>
          </Space>
        </div>

        {/* QuickBooks Integration Card */}
        <Card
          loading={loadingQuickBooks}
          style={{
            borderRadius: 12,
            backgroundColor: token.colorBgContainer,
            borderColor: token.colorBorder,
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Row gutter={24} align="middle">
            <Col flex="auto">
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      backgroundColor: "#1E3A8A",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Plug size={24} color="white" />
                  </div>
                  <div>
                    <Text strong style={{ fontSize: 16, display: "block" }}>
                      QuickBooks Online
                    </Text>
                    <Text
                      style={{ fontSize: 13, color: token.colorTextSecondary }}
                    >
                      Sync payroll data and employee information
                    </Text>
                  </div>
                </div>

                <Divider style={{ margin: "12px 0" }} />

                <div>
                  <Text
                    style={{
                      fontSize: 12,
                      color: token.colorTextSecondary,
                      display: "block",
                      marginBottom: 8,
                    }}
                  >
                    Status
                  </Text>
                  {integrations[0]?.isConnected ? (
                    <Space>
                      <Tag
                        color="success"
                        style={{
                          padding: "4px 12px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <CheckCircle2 size={12} />
                        Connected
                      </Tag>

                      {integrations[0]?.connectedAt && (
                        <Text
                          style={{
                            fontSize: 12,
                            color: token.colorTextTertiary,
                          }}
                        >
                          Connected on{" "}
                          {new Date(
                            integrations[0].connectedAt
                          ).toLocaleString()}
                        </Text>
                      )}
                    </Space>
                  ) : (
                    <>
                      <Tag color="default" style={{ padding: "4px 12px" }}>
                        Not Connected
                      </Tag>
                      <div style={{ marginTop: 8 }}>
                        <Paragraph
                          style={{
                            margin: 0,
                            fontSize: 12,
                            color: token.colorTextSecondary,
                          }}
                        >
                          Get started by clicking the button below to
                          authenticate with your QuickBooks account. You'll be
                          redirected to QuickBooks to grant access.
                        </Paragraph>
                      </div>
                    </>
                  )}
                </div>
              </Space>
            </Col>

            <Col>
              <Space direction="vertical" size="small">
                {!integrations[0]?.isConnected ? (
                  <QuickBooksIntegration />
                ) : (
                  <>
                    <Button
                      type="primary"
                      onClick={() => setShowTestModal(true)}
                      style={{ borderRadius: 6 }}
                    >
                      Test Connection
                    </Button>
                    <Button
                      danger
                      onClick={() => handleDisconnect(integrations[0].id)}
                      style={{ borderRadius: 6 }}
                    >
                      Disconnect
                    </Button>
                  </>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Coming Soon Integrations */}
        <div>
          <Text
            style={{
              fontSize: 14,
              fontWeight: 600,
              display: "block",
              marginBottom: 16,
            }}
          >
            Coming Soon
          </Text>
          <Row gutter={16}>
            {[{ name: "ADP", description: "Payroll management" }].map(
              (item) => (
                <Col xs={24} sm={12} md={8} key={item.name}>
                  <Card
                    style={{
                      borderRadius: 8,
                      backgroundColor: token.colorBgLayout,
                      borderColor: token.colorBorder,
                      opacity: 0.6,
                    }}
                    bodyStyle={{ padding: 16 }}
                  >
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: "100%" }}
                    >
                      <Text strong style={{ fontSize: 13 }}>
                        {item.name}
                      </Text>
                      <Text
                        style={{
                          fontSize: 12,
                          color: token.colorTextSecondary,
                        }}
                      >
                        {item.description}
                      </Text>
                      <Tag
                        color="processing"
                        style={{ marginTop: 8, width: "fit-content" }}
                      >
                        Coming Soon
                      </Tag>
                    </Space>
                  </Card>
                </Col>
              )
            )}
          </Row>
        </div>
      </Space>

      {/* Test Connection Modal */}
      <Modal
        title="Test QuickBooks Connection"
        open={showTestModal}
        onOk={handleTestConnection}
        onCancel={() => setShowTestModal(false)}
        okText="Test Connection"
        cancelText="Cancel"
        confirmLoading={testingConnection}
      >
        <Spin spinning={testingConnection}>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Paragraph>
              We'll verify that your QuickBooks credentials are valid and that
              we can access your account.
            </Paragraph>
            <div
              style={{
                padding: "12px",
                backgroundColor: token.colorInfoBg,
                borderColor: token.colorInfoBorder,
                border: `1px solid ${token.colorInfoBorder}`,
                borderRadius: 6,
              }}
            >
              <Text style={{ fontSize: 12, color: token.colorInfo }}>
                This will test read-only access to your QuickBooks account.
              </Text>
            </div>
          </Space>
        </Spin>
      </Modal>
    </div>
  );
};

export default IntegrationsTab;
